"use strict";
// A member value change here changes the database: the schema mirrors each list into a PostgreSQL
// enum. Display labels are deliberately absent; the interface layer owns display copy.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ledgerCurrencySchema = exports.LEDGER_CURRENCIES = exports.comparisonOutcomeSchema = exports.COMPARISON_OUTCOMES = exports.rowProvenanceSchema = exports.ROW_PROVENANCES = exports.varianceFieldSchema = exports.VARIANCE_FIELDS = exports.RECEIVED_PAPER_VARIANCE_FIELDS = exports.CAPTURED_PAPER_FIELDS = exports.RECEIVED_PAPER_CAPTURE_CHECK_FIELDS = exports.RECEIVED_PAPER_COMPARED_FIELDS = exports.priceRungSchema = exports.PRICE_RUNGS = exports.weightRungSchema = exports.WEIGHT_RUNGS = exports.tradeSideSchema = exports.TRADE_SIDES = exports.closureReasonSchema = exports.CLOSURE_REASONS = exports.chainRoleSchema = exports.CHAIN_ROLES = exports.documentOriginSchema = exports.DOCUMENT_ORIGINS = exports.documentKindSchema = exports.DOCUMENT_KINDS = exports.ledgerKindSchema = exports.LEDGER_KINDS = void 0;
exports.isCapturedPaperField = isCapturedPaperField;
exports.isLedgerKind = isLedgerKind;
exports.isDocumentKind = isDocumentKind;
exports.isDocumentOrigin = isDocumentOrigin;
exports.isChainRole = isChainRole;
exports.isClosureReason = isClosureReason;
exports.isTradeSide = isTradeSide;
exports.isVarianceField = isVarianceField;
exports.isReceivedPaperComparedField = isReceivedPaperComparedField;
exports.isReceivedPaperVarianceField = isReceivedPaperVarianceField;
exports.isRowProvenance = isRowProvenance;
exports.isLedgerCurrency = isLedgerCurrency;
exports.isComparisonOutcome = isComparisonOutcome;
const zod_1 = require("zod");
exports.LEDGER_KINDS = ['purchase', 'sale', 'costs', 'overhead'];
exports.ledgerKindSchema = zod_1.z.enum(exports.LEDGER_KINDS);
exports.DOCUMENT_KINDS = [
    'advance',
    'provisional',
    'final',
    'debit_note',
    'credit_note',
    'cost',
    'pro_forma',
];
exports.documentKindSchema = zod_1.z.enum(exports.DOCUMENT_KINDS);
exports.DOCUMENT_ORIGINS = ['issued', 'received'];
exports.documentOriginSchema = zod_1.z.enum(exports.DOCUMENT_ORIGINS);
exports.CHAIN_ROLES = ['root', 'adjustment', 'standalone'];
exports.chainRoleSchema = zod_1.z.enum(exports.CHAIN_ROLES);
// The PostgreSQL type for these members is `ledger_closure`, on column `closure_reason`.
// `first_invoice_final_mark` is a marked first invoice (D23, register item R9): evidence a live
// invoice_ledger_final_mark row supplies, never a close in its own right (D19).
exports.CLOSURE_REASONS = ['finals', 'cancelled', 'first_invoice_final_mark'];
exports.closureReasonSchema = zod_1.z.enum(exports.CLOSURE_REASONS);
// A side ledger kind is a trade side. The costs and overhead ledgers have no side.
exports.TRADE_SIDES = ['purchase', 'sale'];
exports.tradeSideSchema = zod_1.z.enum(exports.TRADE_SIDES);
// Card 2068: how firmly an expectation figure is known. A weight climbs from the BL's own
// contract share, to a recorded measurement, to a confirmed final. A price starts provisional
// (it can still move with the LME) and becomes fixed either because it is confirmed final or
// because the ticket priced the side as Fixed from the start.
exports.WEIGHT_RUNGS = ['contract', 'measured', 'final'];
exports.weightRungSchema = zod_1.z.enum(exports.WEIGHT_RUNGS);
exports.PRICE_RUNGS = ['provisional', 'fixed'];
exports.priceRungSchema = zod_1.z.enum(exports.PRICE_RUNGS);
// The six fields the received-paper comparison writes variances for. The three remaining
// variance_field members are derived durable exceptions on issued evidence, never a mismatch
// between a received document and the record.
exports.RECEIVED_PAPER_COMPARED_FIELDS = [
    'quantity',
    'unit_price',
    'advance_deduction',
    'amount_due',
    'due_date',
    'beneficiary_bank',
];
// Card 1731: the fields the capture-time check writes and the extraction comparison never does.
// `billed_total` holds the side's expectation against the side's billed total after the captured
// paper; an open row says the paper would take the side past its expected value. Card 1680:
// `provisional_deduction` holds the provisional line a final prints against what it applied to the
// prior provisionals.
exports.RECEIVED_PAPER_CAPTURE_CHECK_FIELDS = [
    'billed_total',
    'provisional_deduction',
];
// Card 1968: every value the received-paper capture stores, in the order the capture form asks
// for them. Six of these are also compared fields; the rest reach no comparison at all, which is
// why a captured cost — which has no expected value to compare against — needs its own read-back.
exports.CAPTURED_PAPER_FIELDS = [
    'issuerCompanyName',
    'documentNumber',
    'issueDate',
    'dueDate',
    'amount',
    'vatAmount',
    'currency',
    'itemDescription',
    'paymentTerm',
    'quantityMt',
    'unitPrice',
    'advanceDeduction',
    'beneficiaryBank',
];
function isCapturedPaperField(value) {
    return typeof value === 'string' && exports.CAPTURED_PAPER_FIELDS.includes(value);
}
// Every variance_field member a received document can carry: the six compared fields plus the
// capture-time check fields. The three remaining members are derived durable exceptions on issued
// evidence.
exports.RECEIVED_PAPER_VARIANCE_FIELDS = [
    ...exports.RECEIVED_PAPER_COMPARED_FIELDS,
    ...exports.RECEIVED_PAPER_CAPTURE_CHECK_FIELDS,
];
exports.VARIANCE_FIELDS = [
    ...exports.RECEIVED_PAPER_VARIANCE_FIELDS,
    'attribution_breach',
    'cash_allocation_breach',
    'settlement_divergence',
];
exports.varianceFieldSchema = zod_1.z.enum(exports.VARIANCE_FIELDS);
// Reuse forbidden: the per-field `derived`/`override` pair on `bl_order_side_valuation` is a
// different enumeration with different members.
exports.ROW_PROVENANCES = ['native', 'projected'];
exports.rowProvenanceSchema = zod_1.z.enum(exports.ROW_PROVENANCES);
// FR-6a. The PostgreSQL type is `comparison_outcome`, on column
// invoice_document_variance.comparison_outcome. Operations have not yet confirmed that an
// unreadable field and an absent field are meaningfully distinct; four members ship because a
// later collapse is cheap and a later split is not.
exports.COMPARISON_OUTCOMES = ['match', 'mismatch', 'unreadable', 'absent'];
exports.comparisonOutcomeSchema = zod_1.z.enum(exports.COMPARISON_OUTCOMES);
exports.LEDGER_CURRENCIES = [
    'USD',
    'EUR',
    'GBP',
    'CNY',
    'JPY',
    'AUD',
    'CAD',
    'CHF',
    'INR',
    'MYR',
    'THB',
];
exports.ledgerCurrencySchema = zod_1.z.enum(exports.LEDGER_CURRENCIES);
function isLedgerKind(value) {
    return exports.LEDGER_KINDS.includes(value);
}
function isDocumentKind(value) {
    return exports.DOCUMENT_KINDS.includes(value);
}
function isDocumentOrigin(value) {
    return exports.DOCUMENT_ORIGINS.includes(value);
}
function isChainRole(value) {
    return exports.CHAIN_ROLES.includes(value);
}
function isClosureReason(value) {
    return exports.CLOSURE_REASONS.includes(value);
}
function isTradeSide(value) {
    return exports.TRADE_SIDES.includes(value);
}
function isVarianceField(value) {
    return exports.VARIANCE_FIELDS.includes(value);
}
function isReceivedPaperComparedField(value) {
    return exports.RECEIVED_PAPER_COMPARED_FIELDS.includes(value);
}
function isReceivedPaperVarianceField(value) {
    return exports.RECEIVED_PAPER_VARIANCE_FIELDS.includes(value);
}
function isRowProvenance(value) {
    return exports.ROW_PROVENANCES.includes(value);
}
function isLedgerCurrency(value) {
    return exports.LEDGER_CURRENCIES.includes(value);
}
function isComparisonOutcome(value) {
    return exports.COMPARISON_OUTCOMES.includes(value);
}
//# sourceMappingURL=vocabulary.js.map